/**
 * Fleet QR Manager — per-unit QR table on /settings/pocket > Fleet.
 *
 * Each row shows the unit's active QR snapshot or a "missing" badge.
 * Clicking a row opens a slide-in Sheet with the full management UI
 * (QrCodesTab — handles regenerate, upload, restore, archive,
 * hard-delete). Filtering by `?filter=missing_qr` URL param lands on
 * just the units lacking an active sticker.
 */

import { useEffect, useMemo, useState, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { QrCode, Search } from 'lucide-react';
import { getFleetQrStatus } from '@/lib/pocketSettingsApi';
import type { FleetUnitQrRow } from '@/types/pocketSettings';
import { QrCodesTab } from '@/components/records/QrCodesTab';

const SOURCE_LABEL: Record<string, string> = {
  generated_v1: 'Generated',
  uploaded: 'Uploaded',
  uploaded_arbitrary: 'Override',
};

export function FleetQrManager() {
  const [params, setParams] = useSearchParams();
  const filter = params.get('filter') || 'all';
  const [rows, setRows] = useState<FleetUnitQrRow[]>([]);
  const [missingTotal, setMissingTotal] = useState(0);
  const [unitTotal, setUnitTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [active, setActive] = useState<FleetUnitQrRow | null>(null);
  const [reloadKey, setReloadKey] = useState(0);

  const refresh = useCallback(() => setReloadKey((k) => k + 1), []);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    getFleetQrStatus(filter === 'missing_qr')
      .then((data) => {
        if (cancelled) return;
        setRows(data.units || []);
        setMissingTotal(data.missing_count || 0);
        setUnitTotal(data.total_units || 0);
      })
      .catch(() => {
        if (cancelled) return;
        setError("Couldn't load fleet status.");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [filter, reloadKey]);

  const filteredRows = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter(
      (r) =>
        r.unit_code.toLowerCase().includes(q) ||
        (r.name || '').toLowerCase().includes(q) ||
        (r.model_code || '').toLowerCase().includes(q),
    );
  }, [rows, search]);

  function handleFilterChange(next: 'all' | 'missing_qr') {
    const newParams = new URLSearchParams(params);
    if (next === 'missing_qr') {
      newParams.set('filter', 'missing_qr');
    } else {
      newParams.delete('filter');
    }
    setParams(newParams, { replace: true });
  }

  return (
    <Card>
      <CardHeader className="flex flex-row items-start justify-between gap-4 space-y-0">
        <div>
          <CardTitle className="text-base font-semibold">Fleet QR codes</CardTitle>
          <p className="text-xs text-muted-foreground mt-1">
            {unitTotal} units · {missingTotal} missing active QR. Click a row
            to manage.
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button
            size="sm"
            variant={filter === 'all' ? 'secondary' : 'outline'}
            onClick={() => handleFilterChange('all')}
          >
            All
          </Button>
          <Button
            size="sm"
            variant={filter === 'missing_qr' ? 'secondary' : 'outline'}
            onClick={() => handleFilterChange('missing_qr')}
          >
            Missing QR ({missingTotal})
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        <div className="flex items-center gap-2 mb-4">
          <Search className="h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Search by unit code, name, or model"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="max-w-sm"
          />
        </div>

        {error ? (
          <p className="text-sm text-destructive">{error}</p>
        ) : loading ? (
          <p className="text-sm text-muted-foreground">Loading…</p>
        ) : filteredRows.length === 0 ? (
          <p className="text-sm text-muted-foreground py-6 text-center">
            {filter === 'missing_qr'
              ? 'Every unit has an active QR. Nothing to fix.'
              : 'No units match your search.'}
          </p>
        ) : (
          <div className="rounded-md border border-border/60 overflow-hidden">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Unit</TableHead>
                  <TableHead>Model</TableHead>
                  <TableHead>QR status</TableHead>
                  <TableHead>Source</TableHead>
                  <TableHead className="text-right">Activated</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredRows.map((row) => (
                  <TableRow
                    key={row.unit_id}
                    className="cursor-pointer hover:bg-muted/50"
                    onClick={() => setActive(row)}
                  >
                    <TableCell>
                      <div className="font-mono text-xs">{row.unit_code}</div>
                      {row.name ? (
                        <div className="text-xs text-muted-foreground">
                          {row.name}
                        </div>
                      ) : null}
                    </TableCell>
                    <TableCell className="font-mono text-xs">
                      {row.model_code || '—'}
                    </TableCell>
                    <TableCell>
                      {row.has_active_qr ? (
                        <Badge variant="secondary">Active</Badge>
                      ) : (
                        <Badge
                          variant="outline"
                          className="border-destructive/40 text-destructive"
                        >
                          Missing
                        </Badge>
                      )}
                    </TableCell>
                    <TableCell>
                      {row.active_qr ? (
                        <Badge variant="outline" className="text-xs">
                          {SOURCE_LABEL[row.active_qr.source] || row.active_qr.source}
                        </Badge>
                      ) : (
                        <span className="text-xs text-muted-foreground">—</span>
                      )}
                    </TableCell>
                    <TableCell className="text-right text-xs tabular-nums text-muted-foreground">
                      {row.active_qr?.activated_at
                        ? new Date(row.active_qr.activated_at).toLocaleDateString()
                        : '—'}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </CardContent>

      <Sheet
        open={active !== null}
        onOpenChange={(open) => {
          if (!open) {
            setActive(null);
            // Refresh the table when the drawer closes — actions inside
            // may have changed the unit's active-QR state.
            refresh();
          }
        }}
      >
        <SheetContent className="w-full sm:max-w-2xl overflow-y-auto">
          <SheetHeader>
            <SheetTitle className="flex items-center gap-2">
              <QrCode className="h-4 w-4" />
              <span className="font-mono">{active?.unit_code}</span>
              {active?.name ? (
                <span className="text-sm font-normal text-muted-foreground">
                  · {active.name}
                </span>
              ) : null}
            </SheetTitle>
          </SheetHeader>
          <div className="mt-4">
            {active ? (
              <QrCodesTab
                unitId={active.unit_id}
                unitCode={active.unit_code}
              />
            ) : null}
          </div>
        </SheetContent>
      </Sheet>
    </Card>
  );
}
